import ReactJsonView, { TypesElement, SymbolsElement } from '@uiw/react-json-view'
import { lightTheme } from '@uiw/react-json-view/light'
import { cn } from 'lib/utils'
import { ComponentPropsWithoutRef } from 'react'
import { TbSquareRoundedMinus, TbSquareRoundedPlus } from 'react-icons/tb'
import { tv, type VariantProps } from 'tailwind-variants'

const jsonViewerRecipe = tv({
  base: '[&_.w-rjv-value[data-type=string]]:break-all',
  variants: {
    variant: {
      solid: 'border border-muted bg-zinc-50 rounded-xl p-4',
    },
  },
})

type JsonViewerRecipeVariants = VariantProps<typeof jsonViewerRecipe>

type JsonViewerProps = JsonViewerRecipeVariants &
  ComponentPropsWithoutRef<'div'> & {
    json?: any
    collapsed?: boolean | number
    enableClipboard?: boolean
  }

const jsonViewerTheme = {
  ...lightTheme,
  '--w-rjv-background-color': 'transparent',
  fontSize: '1em',
  lineHeight: '1.8',
}
const svgProps = {
  style: {
    cursor: 'pointer',
    height: '1.1em',
    width: '1.1em',
    marginRight: 5,
    marginTop: -1,
    opacity: 0.8,
    stroke: 'var(--w-rjv-color)',
  },
}

export function JsonViewer({ json, collapsed = 2, enableClipboard, variant, className, ...props }: JsonViewerProps) {
  const recipeClassnames = jsonViewerRecipe({ variant })

  if (json === null || json === undefined) {
    return '<empty data>'
  }

  if (typeof json !== 'object') {
    return json.toString()
  }

  return (
    <div {...props} className={cn(recipeClassnames, className)}>
      <ReactJsonView
        displayDataTypes={true}
        displayObjectSize={false}
        value={json ?? {}}
        collapsed={collapsed}
        enableClipboard={enableClipboard}
        shortenTextAfterLength={0}
        shouldExpandNodeInitially={shouldExpandNodeInitially}
        style={jsonViewerTheme}
      >
        <ReactJsonView.Arrow render={Arrow} />
        <ReactJsonView.Quote>
          <span />
        </ReactJsonView.Quote>

        {/*
          Null, NaN, and Undefined are displayed as types and cannot be hidden using displayDataTypes=false.
          These values override the display behavior of all other data types.
        */}
        <ReactJsonView.Bigint render={renderValueNotType} />
        <ReactJsonView.Date render={renderValueNotType} />
        <ReactJsonView.False render={renderValueNotType} />
        <ReactJsonView.Int render={renderValueNotType} />
        <ReactJsonView.Map render={renderValueNotType} />
        <ReactJsonView.Set render={renderValueNotType} />
        <ReactJsonView.String render={renderValueNotType} />
        <ReactJsonView.True render={renderValueNotType} />
        <ReactJsonView.Url render={renderValueNotType} />
      </ReactJsonView>
    </div>
  )
}

function renderValueNotType(props: TypesElement<'span'>, { type }) {
  if (type === 'type') {
    return <span />
  }
  if (typeof props.children === 'boolean') {
    return <span {...props}>{props.children.toString()}</span>
  }
  return <span {...props} />
}

function Arrow({ style }: SymbolsElement<'span'> & { 'data-expanded'?: boolean }) {
  /**
   * Interesting behavior with Arrow component:
   *
   * `data-expanded` attribute doesn't always match the real value of whether this section is expanded or collapsed.
   * So currently we look at the `style.transform`, which controls the rotation of the icon
   * and based on this we render the needed icon.
   */
  const isExpanded = style?.transform === 'rotate(-90deg)'
  if (!isExpanded) {
    return <TbSquareRoundedMinus {...svgProps} />
  }
  return <TbSquareRoundedPlus {...svgProps} />
}

function shouldExpandNodeInitially(
  isExpanded: boolean,
  props: { keys: (number | string)[]; value?: unknown; level: number },
): boolean {
  const { value } = props
  const isArray = Array.isArray(value)

  if (isArray) {
    return isExpanded || value.length > 20
  }

  return isExpanded
}
